import { useForm } from 'vee-validate';
import type { Choices, SupportPath } from '@pkg/sanity-utils/types/support';
import type { FAQ } from '@pkg/sanity-utils/types/faq';

export function useSupportPaths(supportPath: SupportPath) {
  const { values, setFieldValue } = useForm();

  const selection1 = computed(() => values.item1Choice);
  const selection2 = computed(() => values.item2Choice);
  const selection3 = computed(() => values.item3Choice);

  watch(selection1, () => {
    setFieldValue('item2Choice', undefined);
    setFieldValue('item3Choice', undefined);
  });
  watch(selection2, () => {
    setFieldValue('item3Choice', undefined);
  });

  const choice1 = computed(() => supportPath?.itemChoices?.find(choice => choice.title === selection1.value));
  const choice2 = computed(() => choice1.value?.itemChoices?.find(choice => choice.title === selection2.value));
  const choice3 = computed(() => choice2.value?.itemChoices?.find(choice => choice.title === selection3.value));

  const chosenPath = computed(() => finalChoice([choice3.value, choice2.value, choice1.value]));

  const pathData = computed(() => ({
    faqPreTitle: chosenPath.value?.faqPreTitle || undefined,
    faqTitle: chosenPath.value?.faqTitle || undefined,
    faqItems: chosenPath.value?.faqsToDisplay?.reduce((acc, faq) => {
      return [
        ...(acc || []),
        ...(faq.items || []),
      ];
    }, [] as FAQ['items']),
    formPreTitle: chosenPath.value?.formPreTitle || undefined,
    formTitle: chosenPath.value?.formTitle || undefined,
    formToDisplay: chosenPath.value?.formToDisplay,
    formImage: chosenPath.value?.formImage,
    formTriggers: chosenPath.value?.contactTrigger || undefined,
  }));

  function choicesToSelectOptions(choices: Array<{ title?: null | string }>) {
    return choices
      .filter<{ title: string }>((choice): choice is { title: string } => typeof choice.title === 'string')
      .map(choice => ({ value: choice.title, title: choice.title }));
  }

  return {
    choice1,
    choice2,
    choice3,
    chosenPath,
    pathData,
    selection1,
    selection2,
    selection3,
    choicesToSelectOptions,
  };
}

function finalChoice(choices: Array<Choices | undefined>) {
  for (const choice of choices) {
    if (choice && !choice?.hasAnotherQuestion) {
      return choice;
    }
  }

  return undefined;
}
